"use client"

import { LockIcon } from "@/components/icons/pixel-icons"
import type { ItemDefinition } from "@/types/item.types"
import { CodexArtPlaceholder } from "../codex-art-placeholder"

// Rarity colors
const RARITY_COLORS = {
  common: "#9CA3AF",
  rare: "#3B82F6",
  epic: "#A855F7",
  legendary: "#F59E0B",
  unique: "#F97316",
}

interface ItemThumbnailProps {
  item: ItemDefinition
  isSelected: boolean
  isLocked: boolean
  onClick: () => void
}

export const ItemThumbnail = ({
  item,
  isSelected,
  isLocked,
  onClick,
}: ItemThumbnailProps) => {
  // Rarity is part of what discovery reveals, so a locked tile gets the neutral
  // border rather than a legendary-orange one that hints at what is inside
  const rarityColor = isLocked ? "#475569" : RARITY_COLORS[item.rarity]

  return (
    <button
      onClick={onClick}
      className={`relative aspect-square rounded-lg overflow-hidden transition-all ${
        isSelected
          ? "ring-2 ring-amber-400 ring-offset-2 ring-offset-slate-900 scale-105"
          : "hover:scale-102"
      } ${isLocked ? "cursor-default" : "cursor-pointer"}`}
      style={{
        backgroundColor: "#2d3748",
        boxShadow: isSelected
          ? `0 0 12px ${rarityColor}`
          : `inset 0 0 0 1px ${rarityColor}40`,
      }}
      title={isLocked ? "Not yet discovered" : item.name}
      disabled={isLocked}
    >
      {/* Items you have never held stay hidden; otherwise the art, or a named
          placeholder when the item has no art */}
      {isLocked ? (
        <CodexArtPlaceholder dimmed />
      ) : item.image ? (
        <div className="w-full h-full p-2">
          <img
            src={item.image}
            alt={item.name}
            className="w-full h-full object-contain"
            style={{ imageRendering: "pixelated" }}
          />
        </div>
      ) : (
        <CodexArtPlaceholder name={item.name} />
      )}

      {/* Locked overlay */}
      {isLocked && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60">
          <LockIcon size={32} className="text-gray-400" />
        </div>
      )}

      {/* Rarity indicator (small dot in corner) */}
      {!isLocked && (
        <div
          className="absolute bottom-1 right-1 w-2 h-2 rounded-full"
          style={{ backgroundColor: rarityColor }}
        />
      )}
    </button>
  )
}
